'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';

import { cn } from '@kit/ui/utils';

type HeaderTone = 'light' | 'dark' | 'image';
type HeaderState = { hidden: boolean; scrolled: boolean; tone: HeaderTone };

/** Keeps the real navigation accessible while responding to the surface beneath it. */
export function SiteHeaderFrame({ children }: { children: ReactNode }) {
  const headerRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
  const immersive =
    pathname.startsWith('/thank-you/') ||
    ['/', '/axcelerate', '/pricing', '/contact'].includes(pathname);
  const [state, setState] = useState<HeaderState>({
    hidden: false,
    scrolled: false,
    tone: 'light',
  });

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const readTone = createSurfaceReader(header);
    let previousY = window.scrollY;
    let direction = 0;
    let travel = 0;
    let hidden = false;
    let frame = 0;

    function update() {
      frame = 0;
      if (document.hidden || !header) return;

      // Clamping prevents elastic scrolling from being treated as a direction change.
      const maxY = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const y = Math.max(0, Math.min(window.scrollY, maxY));
      const delta = y - previousY;
      const nextDirection = Math.sign(delta);
      const height = header.offsetHeight;
      const menuOpen = Boolean(header.querySelector('[aria-expanded="true"]'));
      const locked = header.contains(document.activeElement) || menuOpen;

      if (nextDirection !== 0) {
        travel =
          nextDirection === direction
            ? travel + Math.abs(delta)
            : Math.abs(delta);
        direction = nextDirection;
      }

      if (y <= height + 24 || locked || reducedMotion.matches) {
        hidden = false;
        travel = 0;
      } else if (direction === 1 && travel >= 24) {
        hidden = true;
      } else if (direction === -1 && travel >= 8) {
        hidden = false;
      }

      previousY = y;
      const next: HeaderState = {
        hidden,
        scrolled: y > 12,
        tone: immersive && y <= 12 ? 'image' : readTone(height),
      };
      setState((current) => {
        if (menuOpen) next.tone = current.tone;
        return current.hidden === next.hidden &&
          current.scrolled === next.scrolled &&
          current.tone === next.tone
          ? current
          : next;
      });
    }

    function schedule() {
      if (!frame && !document.hidden)
        frame = window.requestAnimationFrame(update);
    }

    // Menu content can be portalled: its trigger's expanded state keeps the header visible.
    const menuObserver = new MutationObserver(schedule);
    menuObserver.observe(header, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(header);
    const content = document.getElementById('site-content');
    if (content) resizeObserver.observe(content);

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('visibilitychange', schedule);
    header.addEventListener('focusin', schedule);
    header.addEventListener('focusout', schedule);
    reducedMotion.addEventListener('change', schedule);
    schedule();

    return () => {
      window.cancelAnimationFrame(frame);
      menuObserver.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
      header.removeEventListener('focusin', schedule);
      header.removeEventListener('focusout', schedule);
      reducedMotion.removeEventListener('change', schedule);
    };
  }, [pathname, immersive]);

  return (
    <header
      ref={headerRef}
      data-site-header
      data-header-immersive={immersive}
      data-header-hidden={state.hidden}
      data-header-scrolled={state.scrolled}
      data-header-tone={immersive && !state.scrolled ? 'image' : state.tone}
      className={cn(
        'site-header sticky top-0 z-30 w-full',
        (state.tone !== 'light' || (immersive && !state.scrolled)) && 'dark',
      )}
    >
      {children}
    </header>
  );
}

function createSurfaceReader(header: HTMLElement) {
  // CSS colours can be oklch(), rgb(), or named tokens. A single cached pixel
  // normalises only CSS paint colours; photographs are never sampled or fetched.
  const context = document.createElement('canvas').getContext('2d', {
    willReadFrequently: true,
  });
  const colours = new Map<string, number[]>();

  function colour(value: string) {
    const cached = colours.get(value);
    if (cached) return cached;
    if (!context) return [255, 255, 255, 255];
    context.clearRect(0, 0, 1, 1);
    context.fillStyle = value;
    context.fillRect(0, 0, 1, 1);
    const rgba = Array.from(context.getImageData(0, 0, 1, 1).data);
    colours.set(value, rgba);
    return rgba;
  }

  function toneAt(x: number, y: number): HeaderTone {
    const underneath = document
      .elementsFromPoint(x, y)
      .find(
        (element) =>
          !header.contains(element) && element !== document.documentElement,
      );
    if (!underneath) return 'light';

    // Explicit section semantics win over a nested card or a decorative gradient.
    const section = underneath.closest('section, main, footer') ?? underneath;
    const explicitTone = section
      .closest('[data-header-tone]')
      ?.getAttribute('data-header-tone');
    if (
      explicitTone === 'light' ||
      explicitTone === 'dark' ||
      explicitTone === 'image'
    ) {
      return explicitTone;
    }
    if (section.closest('[data-website-full-bleed]')) return 'image';
    if (section.closest('.dark')?.closest('[data-marketing-site]'))
      return 'dark';
    if (
      underneath instanceof HTMLImageElement ||
      underneath instanceof HTMLVideoElement
    ) {
      return 'image';
    }

    let remaining = 1;
    let red = 0;
    let green = 0;
    let blue = 0;
    for (
      let element: Element | null = section;
      element && remaining > 0.01;
      element = element.parentElement
    ) {
      const rgba = colour(window.getComputedStyle(element).backgroundColor);
      const alpha = (rgba[3] ?? 0) / 255;
      red += (rgba[0] ?? 0) * alpha * remaining;
      green += (rgba[1] ?? 0) * alpha * remaining;
      blue += (rgba[2] ?? 0) * alpha * remaining;
      remaining *= 1 - alpha;
    }
    const luminance =
      (0.2126 * red + 0.7152 * green + 0.0722 * blue + 255 * remaining) / 255;
    return luminance < 0.5 ? 'dark' : 'light';
  }

  return (height: number): HeaderTone => {
    // Stable viewport coordinates continue to work when the header is translated away.
    const y = Math.min(window.innerHeight - 1, Math.max(1, height / 2));
    const width = document.documentElement.clientWidth;
    const tones = [0.2, 0.5, 0.8].map((fraction) =>
      toneAt(width * fraction, y),
    );
    const darkCount = tones.filter((tone) => tone !== 'light').length;
    if (darkCount < 2) return 'light';
    return tones.includes('image') ? 'image' : 'dark';
  };
}
